// Updated Photos Final
import React, { useState } from 'react';
import { ContactCard } from '@components/Card';
import { MailIcon, ChatIcon, ExternalLinkIcon } from '@components/Icons';
import Button from '@components/Button';
import styles from './ContactPage.module.css';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  industry: string;
  fleetSize: string;
  message: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    industry: '',
    fleetSize: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://formspree.io/f/xaqnynvr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          company: formData.company,
          industry: formData.industry,
          fleetSize: formData.fleetSize,
          message: formData.message,
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          industry: '',
          fleetSize: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Page Header */}
      <section className="page-header">
        <h1 className="page-title">How can we help?</h1>
        <p className="page-subtitle">Get in touch with our sales and support teams for demos, onboarding support, or product questions.</p>
      </section>

      {/* Contact Cards */}
      <section className={styles.contactSection}>
        <div className="container">
          <div className={styles.contactGrid} id="sales">
            <ContactCard
              icon={<MailIcon />}
              title="Sales"
              description="Speak to our sales team about partnerships, enterprise contracts, or to request a demo of Laminar Labs."
              actionLabel="Talk to sales"
              actionHref="#demo"
            />
            <ContactCard
              icon={<ChatIcon />}
              title="Help & support"
              description="Ask product questions, report problems, or leave feedback. Our support team typically responds within 2 hours."
              actionLabel="Contact support"
              actionHref="#demo"
            />
          </div>

          {/* Additional Contact Info */}
          <div className={styles.contactInfoGrid}>
            <div className={styles.contactInfoItem}>
              <h4>Schedule a demo</h4>
              <p>See Laminar Labs in action with a personalized walkthrough tailored to your operations.</p>
              <a href="#demo">
                Request demo
                <ExternalLinkIcon />
              </a>
            </div>
            <div className={styles.contactInfoItem}>
              <h4>General communication</h4>
              <p>For other queries, please get in touch with us via email.</p>
              <a href="mailto:vc35@rice.edu">
                <MailIcon className={styles.inlineIcon} />
                vc35@rice.edu
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section className={styles.demoSection} id="demo">
        <div className="container">
          <div className={styles.formWrapper}>
            <div className="section-header">
              <div className="section-label">Request a Demo</div>
              <h2 className="section-title">See Laminar in action</h2>
              <p className="section-subtitle">Fill out the form below and we'll reach out to schedule your personalized demo.</p>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>First name</label>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Last name</label>
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Smith"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Work email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Company</label>
                <input
                  type="text"
                  name="company"
                  placeholder="Acme Energy Services"
                  value={formData.company}
                  onChange={handleChange}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Industry</label>
                <select name="industry" value={formData.industry} onChange={handleChange}>
                  <option value="" disabled>Select your industry</option>
                  <option value="compression">Compression Services</option>
                  <option value="oil-gas">Oil & Gas</option>
                  <option value="mining">Mining</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Fleet size</label>
                <select name="fleetSize" value={formData.fleetSize} onChange={handleChange}>
                  <option value="" disabled>Number of units/technicians</option>
                  <option value="1-50">1-50</option>
                  <option value="51-200">51-200</option>
                  <option value="201-500">201-500</option>
                  <option value="500+">500+</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>What are you most interested in?</label>
                <textarea
                  name="message"
                  placeholder="Tell us about your current challenges and what you're hoping to achieve..."
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>

              {submitStatus === 'success' ? (
                <div className={styles.successMessage}>
                  ✓ Thank you! We'll be in touch soon.
                </div>
              ) : (
                <>
                  <Button type="submit" variant="primary" size="large" className={styles.submitBtn} disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Request Demo'}
                    {!isSubmitting && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14"/>
                        <path d="M12 5l7 7-7 7"/>
                      </svg>
                    )}
                  </Button>
                  
                  {submitStatus === 'error' && (
                    <div className={styles.errorMessage}>
                      Something went wrong. Please try again.
                    </div>
                  )}
                </>
              )}

              <p className={styles.privacyNote}>
                By submitting this form, you agree to our <a href="#">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Office Location */}
      <section className={styles.locationSection}>
        <div className="container">
          <div className={styles.locationGrid}>
            <div className={styles.locationText}>
              <div className="section-label">Headquarters</div>
              <h2>Houston, Texas</h2>
              <div className={styles.address}>
                <p>Laminar Labs, Inc.</p>
                <p>Rice University</p>
                <p>6100 Main St</p>
                <p>Houston, TX 77005</p>
              </div>
            </div>
            <div className={styles.mapContainer}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3464.837648804689!2d-95.40307638489373!3d29.717421081997536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8640c087b39b7e9f%3A0x3a6a0b63d3f84a22!2sRice%20University!5e0!3m2!1sen!2sus!4v1704456000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Rice University Location"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;